'use client';

import Cookies from 'js-cookie';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

type BookingConfirmationPanelProps = {
  autoConfirmModalFormEvent: string;
  autoConfirmModalId: string;
  autoConfirmSubmitButtonId: string;
  autoConfirmFormId: string;
  triggerFlowKey: string;
  // signupConfirmModalEvent: string;
  signupConfirmModalEvent: string;
  signupConfirmModalId: string;
};

/**
 * NOTE:
 * This component intentionally uses MutationObserver
 * because modal rendering & lifecycle are controlled externally (Contentful).
 * Do NOT refactor into React state without understanding the CMS integration.
 */

const BOOKING_RESERVATION_NUMBER_REGEX = /^\d+$/; // e.g., "12345-67890"

const BookingConfirmationPanel = ({signupConfirmModalId, autoConfirmModalId, autoConfirmFormId, autoConfirmModalFormEvent, signupConfirmModalEvent, autoConfirmSubmitButtonId, triggerFlowKey="reservation_no"}:BookingConfirmationPanelProps) => {
    
    const bookingReservationKey = triggerFlowKey;
    const isInContentfulStudio = (typeof window !== "undefined") && window.self !== window.top;
    const isProbablyInEditorMode = isInContentfulStudio;

    //  queryParams contains a particular key then only trigger confirmation flow
    // e.g., ?confirmBooking=true

    const searchParams = useSearchParams();
    const confirmBooking = searchParams.get('bookingConfirmation') === 'true';
    // console.log("BookingConfirmationPanel rendered with confirmBooking:", confirmBooking);

    useEffect(() => {
      if (!confirmBooking) return;

      if (confirmBooking) {
        const reservation_number = Cookies.get(bookingReservationKey); //always a string or undefined
        const isValidReservationId =
          typeof reservation_number === 'string' &&
          BOOKING_RESERVATION_NUMBER_REGEX.test(reservation_number);
 
        if (!isValidReservationId) {
          Cookies.remove(bookingReservationKey);
          // trigger signup + confirm flow and open it's modal form
          const observer = new MutationObserver(() => {
            const modalEl = document.querySelector(`#${signupConfirmModalId}`) as HTMLDivElement | null;
            if (modalEl) {
              window.dispatchEvent(new Event(signupConfirmModalEvent));
              observer.disconnect();
            }
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });

          // Timeout to avoid infinite observation
          setTimeout(() => {
            observer.disconnect();
          }, 50000);

          return () => observer.disconnect(); 
        }else{
          /**
           * 1. detect modal is mounted in the DOM
           * 2. if yes, trigger the modal open event else wait for it to be mounted
           * 3. simulate the form submission 
           * 4. disconnect the observer and remove the cookie on successful submission
           * 5. timeout and disconnect the observer after 10 seconds
           */

          let hasSubmitted = false;
          let modalOpened = false;
          const observer = new MutationObserver(() => {
            const modalEl = document.querySelector(`#${autoConfirmModalId}`) as HTMLDivElement | null;
            if(!modalEl) return;

            if (modalEl && !modalOpened) {
              window.dispatchEvent(new Event(autoConfirmModalFormEvent));
              modalOpened = true;
            }

            const formEl = document.querySelector(`form[id="${autoConfirmFormId}"]`) as HTMLFormElement | null;
            const submitBtn = document.querySelector(`button[id="${autoConfirmSubmitButtonId}"]`) as HTMLButtonElement | null;
            if (formEl && submitBtn && !hasSubmitted) {
              setTimeout(() => {
                if (hasSubmitted) return;
                hasSubmitted = true;
                formEl.requestSubmit(submitBtn);
              } , 10);
              return;
            }
            console.log(formEl , submitBtn, hasSubmitted, "checking the values");
            if (!formEl && !submitBtn && hasSubmitted) {
              console.log("Booking confirmation form submitted.");
              Cookies.remove(bookingReservationKey);
              observer.disconnect();
            }
          });
          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
          // timeout to avoid infinite observation
          setTimeout(() => {
              observer.disconnect();
              Cookies.remove(bookingReservationKey);
          }, 50000);
          return () => observer.disconnect();
        }
      }

    },[searchParams]);

  return (
    <div style={{ display: isProbablyInEditorMode ? "block" : "none" }} className="w-screen p-3 align-center" data-testid="BookingConfirmationPanel">
      {"Enter the confirmation flow configuration details to enable automatic booking confirmation"}
    </div>);
};

export default BookingConfirmationPanel;